import * as z from 'zod'

import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionSchema } from 'src/shared/features/users/schemas/profession'
import type { AppraiserTransactionTypeWithCustom } from 'src/shared/schemas/referrals/referral-fields/appraiser/appraiser-transaction-type'
import { appraiserTransactionTypeSchema } from 'src/shared/schemas/referrals/referral-fields/appraiser/appraiser-transaction-type'
import type { AttorneyTransactionTypeWithCustom } from 'src/shared/schemas/referrals/referral-fields/attorney/attorney-transaction-type'
import { attorneyTransactionTypeSchema } from 'src/shared/schemas/referrals/referral-fields/attorney/attorney-transaction-type'
import type { ContractStatus } from 'src/shared/schemas/referrals/referral-fields/attorney/contract-status'
import { contractStatusSchema } from 'src/shared/schemas/referrals/referral-fields/attorney/contract-status'
import type { ProjectType } from 'src/shared/schemas/referrals/referral-fields/contractor/project-type'
import { projectTypeSchema } from 'src/shared/schemas/referrals/referral-fields/contractor/project-type'
import type { CreditScore } from 'src/shared/schemas/referrals/referral-fields/loan-officer/credit-score'
import { creditScoreSchema } from 'src/shared/schemas/referrals/referral-fields/loan-officer/credit-score'
import type { EmploymentStatus } from 'src/shared/schemas/referrals/referral-fields/loan-officer/employment-status'
import { employmentStatusSchema } from 'src/shared/schemas/referrals/referral-fields/loan-officer/employment-status'
import type { LoanPurpose } from 'src/shared/schemas/referrals/referral-fields/loan-officer/loan-purpose'
import { loanPurposeSchema } from 'src/shared/schemas/referrals/referral-fields/loan-officer/loan-purpose'
import type { LoanType } from 'src/shared/schemas/referrals/referral-fields/loan-officer/loan-type'
import { loanTypeSchema } from 'src/shared/schemas/referrals/referral-fields/loan-officer/loan-type'
import type { RefinanceReason } from 'src/shared/schemas/referrals/referral-fields/loan-officer/refinance-reason'
import { refinanceReasonSchema } from 'src/shared/schemas/referrals/referral-fields/loan-officer/refinance-reason'
import type { ClientType } from 'src/shared/schemas/referrals/referral-fields/real-estate-agent/client-type'
import { clientTypeSchema } from 'src/shared/schemas/referrals/referral-fields/real-estate-agent/client-type'
import type { PropertyType } from 'src/shared/schemas/referrals/referral-fields/shared/property-type'
import { propertyTypeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/property-type'
import type { Timeframe } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import { timeframeSchema } from 'src/shared/schemas/referrals/referral-fields/shared/timeframe'
import type { DateFlexibility } from 'src/shared/schemas/referrals/referral-fields/title-company-rep/date-flexibility'
import { dateFlexibilitySchema } from 'src/shared/schemas/referrals/referral-fields/title-company-rep/date-flexibility'
import type { TitleTransactionTypeWithCustom } from 'src/shared/schemas/referrals/referral-fields/title-company-rep/title-transaction-type'
import { titleTransactionTypeSchema } from 'src/shared/schemas/referrals/referral-fields/title-company-rep/title-transaction-type'

import type { ClaimItem } from './referral-types'

// --- Send Referral Form Schemas --- //

export const clientInfoSchema = z
  .object({
    clientFirstName: z.string().min(1, 'Client first name is required'),
    clientLastName: z.string().min(1, 'Client last name is required'),
    clientEmail: z.string().email('Invalid email address').optional(),
    clientPhone: z.string().min(10, 'Phone number must be at least 10 digits').optional(),
  })
  .refine((data) => data.clientEmail || data.clientPhone, {
    message: 'Either email or phone number must be provided',
    path: ['clientPhone'],
  })

// --- Referral Data Schema (used in tests) --- //

export const referralSchema = z.object({
  // Referral Object Data
  id: z.number(),
  referredProfessions: z.array(professionSchema),
  professionsApproved: z.array(professionSchema),
  createdByUserId: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  isClosed: z.boolean(),
  shortAddress: z.string(),
  fullAddress: z.string(),
  interestedReferralShareCount: z.number(),
  referralTotalPoints: z.number(),

  // Consumer ID (added)
  consumerId: z.string().optional(),

  // Client Information (kept for backward compatibility)
  clientType: clientTypeSchema.optional(),
  clientFirstName: z.string().optional(),
  clientLastName: z.string().optional(),
  clientEmail: z.string().email().optional(),
  clientPhone: z.string().optional(),

  // Property Information
  propertyStreetAddress1: z.string().optional(),
  propertyStreetAddress2: z.string().optional(),
  propertyCity: z.string().optional(),
  propertyState: z.string().optional(),
  propertyZip: z.string().optional(),
  propertyType: propertyTypeSchema.optional(),
  desiredLocations: z.array(z.string()).optional(),

  // RE Agent (Buy) Information
  priceRange: z.object({ min: z.number(), max: z.number() }).optional(),
  timeframe: timeframeSchema.optional(),

  // RE Agent (Lease) Information
  budget: z.object({ min: z.number(), max: z.number() }).optional(),
  bedrooms: z.number().optional(),
  bathrooms: z.number().optional(),

  // Loan Officer Information
  loanPurpose: loanPurposeSchema.optional(),
  exactPrice: z.number().optional(),
  downPaymentEstimate: z.number().optional(),
  incomeEstimate: z.number().optional(),
  assetsEstimate: z.number().optional(),
  employmentStatus: employmentStatusSchema.optional(),
  loanType: loanTypeSchema.optional(),
  creditScoreEstimate: creditScoreSchema.optional(),
  isPreApproved: z.boolean().optional(),
  refinanceReason: refinanceReasonSchema.optional(),
  mortgageBalance: z.number().optional(),

  // Insurance Agent Information
  dwellingCoverage: z.number().optional(),
  liabilityCoverage: z.number().optional(),
  personalPropertyCoverage: z.number().optional(),
  deductibleAmount: z.number().optional(),
  additionalEndorsements: z.number().optional(),
  carrierName: z.string().optional(),
  coverageLevelDetails: z.string().optional(),
  pastClaims: z.array(z.any()).optional(), // You might want to create a specific schema for ClaimItem

  // Attorney Information
  attorneyTransactionType: attorneyTransactionTypeSchema.optional(),
  contractStatus: contractStatusSchema.optional(),

  // Contractor Information
  projectType: projectTypeSchema.optional(),
  squareFootageEstimate: z.number().optional(),
  descriptionOfWork: z.string().optional(),
  materialPreferences: z.string().optional(),
  desiredStartDate: z.date().optional(),
  desiredEndDate: z.date().optional(),

  // Home Inspector Information
  yearBuilt: z.number().optional(),
  knownPropertyIssues: z.string().optional(),
  additionalTestNeeded: z.string().optional(),

  // Appraiser Information
  appraiserTransactionType: appraiserTransactionTypeSchema.optional(),
  lotSize: z.number().optional(),
  uniqueFeatures: z.string().optional(),
  estimatedPropertyValue: z.number().optional(),
  approvedLoanAmount: z.number().optional(),

  // Title Rep Information
  titleTransactionType: titleTransactionTypeSchema.optional(),
  desiredClosingDate: z.date().optional(),
  dateFlexibility: dateFlexibilitySchema.optional(),
  additionalServices: z.array(z.string()).optional(),
})

export interface ReferralData {
  // Consumer ID (added)
  consumerId?: string

  // Client Information (kept for backward compatibility)
  clientType?: ClientType
  clientFirstName?: string
  clientLastName?: string
  clientEmail?: string
  clientPhone?: string

  // Property Information
  propertyStreetAddress1?: string
  propertyStreetAddress2?: string
  propertyCity?: string
  propertyState?: string
  propertyZip?: string
  propertyType?: PropertyType
  desiredLocations?: string[]

  // RE Agent (Buy) Information
  priceRange?: { min: number; max: number }
  timeframe?: Timeframe

  // RE Agent (Lease) Information
  budget?: { min: number; max: number }
  bedrooms?: number
  bathrooms?: number

  // Loan Officer Information
  loanPurpose?: LoanPurpose
  exactPrice?: number
  downPaymentEstimate?: number
  incomeEstimate?: number
  assetsEstimate?: number
  employmentStatus?: EmploymentStatus
  loanType?: LoanType
  creditScoreEstimate?: CreditScore
  isPreApproved?: boolean
  refinanceReason?: RefinanceReason
  mortgageBalance?: number

  // Insurance Agent Information
  dwellingCoverage?: number
  liabilityCoverage?: number
  personalPropertyCoverage?: number
  deductibleAmount?: number
  additionalEndorsements?: number
  carrierName?: string
  coverageLevelDetails?: string
  pastClaims?: ClaimItem[]

  // Attorney Information
  attorneyTransactionType?: AttorneyTransactionTypeWithCustom
  contractStatus?: ContractStatus

  // Contractor Information
  projectType?: ProjectType
  squareFootageEstimate?: number
  descriptionOfWork?: string
  materialPreferences?: string
  desiredStartDate?: Date
  desiredEndDate?: Date

  // Home Inspector Information
  yearBuilt?: number
  knownPropertyIssues?: string
  additionalTestNeeded?: string

  // Appraiser Information
  appraiserTransactionType?: AppraiserTransactionTypeWithCustom
  lotSize?: number
  uniqueFeatures?: string
  estimatedPropertyValue?: number
  approvedLoanAmount?: number

  // Title Rep Information
  titleTransactionType?: TitleTransactionTypeWithCustom
  desiredClosingDate?: Date
  dateFlexibility?: DateFlexibility
  additionalServices?: string[]
}

export interface Referral extends ReferralData {
  id: number
  referredProfessions: Profession[]
  professionsApproved: Profession[]
  createdByUserId: number
  createdAt: Date
  updatedAt: Date
  isClosed: boolean
  shortAddress: string
  fullAddress: string
  interestedReferralShareCount: number
  referralTotalPoints: number
}

export type ReferralDataField = keyof ReferralData

export type ClientInfoType = z.infer<typeof clientInfoSchema>
